/**
 * BLU-877 — the printer format a PRINT AGENT declares, and the ONE derivation
 * of "what filename does that format serve".
 *
 * WHAT THE VALUE MEANS: a DEVICE fact about one physical print PC. ThreadHeads
 * HQ runs 11 print PCs at a single location across two device contracts
 * (Brother GTX eats `.arx4`, Kora eats `.png`), so a scalar per-LOCATION format
 * cannot describe the floor. The agent install IS the device, so the agent row
 * is the only honest owner of the value.
 *
 * `null` / undeclared is NOT an error and NOT "no format". It means "this agent
 * has not said", and the serve path answers it with today's behaviour
 * byte-for-byte: the full `PW_CUSTOM_EXTENSIONS` customs fan-out (whose
 * `.arx4`-beats-`.png` precedence is itself a device contract) and the
 * catalogue's standards filename verbatim. Never hard-default a declaration
 * into existence — narrowing seven extensions to one for a floor that never
 * declared silently 404s every custom stored as another raster type.
 *
 * This module is the single home of the enum for the whole phase: router,
 * manifest build, batch gate, standards heal and print-file self-heal all
 * import from here.
 */
import { z } from 'zod';
/**
 * The closed vocabulary. Two ASCII literals, compared with `===` — never
 * case-folded, never normalized, never interpolated into a storage key as free
 * text. A value outside this list is not a format, it is untrusted input.
 */
export const PRINTER_FORMATS = ['arx4', 'png'];
export const printerFormatSchema = z.enum(PRINTER_FORMATS);
/**
 * Parse an untrusted value (a DB column, an envelope line, a wire field) into a
 * `PrinterFormat`, or `null`.
 *
 * Implemented as two literal `===` comparisons on purpose. A `includes()` over
 * a widened `string`, a `toLowerCase()` fold or a regex would all quietly
 * accept a near-miss (`'PNG'`, `' png'`, `'png '`) that no other layer in this
 * phase agrees is a format — and this value ends up selecting a candidate that
 * becomes part of a storage key and a presigned credential. Anything that is
 * not exactly one of the two literals is `null` = undeclared.
 */
export function parsePrinterFormat(value) {
    if (value === 'arx4')
        return 'arx4';
    if (value === 'png')
        return 'png';
    return null;
}
/**
 * The answer for a location with NO active declaring agent (the empty fleet).
 *
 * This is DELIBERATELY NOT the answer for a FAILED fleet read. An error-path
 * default that equals the happy-path default is indistinguishable from a real
 * empty fleet, so a transient DB failure would silently produce a confident
 * wrong answer (review finding S5). Plan 84-02 gives the failed read its own
 * untrusted sentinel that a fingerprint cannot match; do not reuse this
 * constant for it.
 */
export const DEFAULT_PRINTER_FORMATS = ['arx4'];
/**
 * The ONE answer in this repo to "what filename does this format serve".
 *
 * The router (serve path), the production-manifest build, the BLU-827 standards
 * heal and the print-file self-heal all call this. A second copy is exactly how
 * a PRODUCER and a CONSUMER drift into disagreeing about the same garment: the
 * manifest promises `X.png`, the serve path signs `X.arx4`, and the floor gets
 * a 404 that looks like missing artwork. If you need this logic somewhere new,
 * import it — do not re-derive it.
 *
 * - `'png'` → the stem (everything before the LAST dot) with `.png`. A dotless
 *   name gets `.png` appended. Split on the last dot, never a substring
 *   replace: `replace('.arx4', '.png')` mangles a stem that merely CONTAINS the
 *   token, and a global replace mangles it twice.
 * - `'arx4'` and `null` → the catalogue name VERBATIM. That is BLU-825's
 *   found-file-beats-missing-metadata rule (D-15) unchanged: the catalogue value
 *   is what a producer actually wrote, and an undeclared reader has no standing
 *   to rewrite it.
 */
export function servedPrintFileName(catalogueName, format) {
    if (format !== 'png')
        return catalogueName;
    const dot = catalogueName.lastIndexOf('.');
    return dot > 0 ? `${catalogueName.slice(0, dot)}.png` : `${catalogueName}.png`;
}
